"use client";

/**
 * ListCardSkeleton - shimmer placeholder matching ListCard's grid.
 *
 * Used by the list-style render in ComparisonStream while a category
 * search is still streaming. The skeleton occupies the same physical
 * space as a real ListCard, so when a real product arrives and
 * displaces a skeleton, the page does not shift. Pure presentational:
 * no props, no state.
 *
 * Visual contract (locked to mio-list-streaming-mockup.html "State A/B"):
 *   - Same 40px / 88px / 1fr / auto grid as ListCard (gap-4)
 *   - Same rounded-2xl border, same surface tint, same padding (p-4)
 *   - All inner blocks (position chip, image well, brand label, title,
 *     "where" line, price label, price value, compare link) render as
 *     shimmer bars sized to roughly match their real counterparts.
 *
 * Position number is rendered as a small numeric chip so the row
 * order is preserved even during loading; the rest is shimmer.
 *
 * Animation keyframes are injected once at module-eval time so the
 * skeleton is portable to any list-style surface without touching
 * tailwind.config.ts. Same pattern as ThinkingPill.tsx.
 */

interface Props {
  position: number;
}

export function ListCardSkeleton({ position }: Props) {
  return (
    <article
      className="relative flex items-stretch gap-4 rounded-2xl border border-white/8 bg-white/[0.02] p-4"
      aria-hidden="true"
    >
      <div className="flex shrink-0 items-center justify-center self-start rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground">
        {position}
      </div>

      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg sm:h-24 sm:w-24 mio-shimmer" />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="h-2.5 w-14 rounded mio-shimmer" />
        <div className="h-4 w-4/5 rounded mio-shimmer" />
        <div className="h-3 w-1/2 rounded mio-shimmer" />
      </div>

      <div className="flex shrink-0 flex-col items-end justify-center gap-1.5 pl-2">
        <div className="h-2.5 w-20 rounded mio-shimmer" />
        <div className="h-4 w-16 rounded mio-shimmer" />
        <div className="h-3 w-20 rounded mio-shimmer" />
      </div>
    </article>
  );
}

/**
 * Keyframes injected once on the client. Same approach as ThinkingPill.
 */
if (typeof document !== "undefined") {
  const STYLE_ID = "mio-list-skeleton-shimmer";
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .mio-shimmer {
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.04) 0%,
          rgba(255, 255, 255, 0.10) 50%,
          rgba(255, 255, 255, 0.04) 100%
        );
        background-size: 200% 100%;
        animation: mio-shimmer-slide 1.6s infinite linear;
      }
      @keyframes mio-shimmer-slide {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
      }
    `;
    document.head.appendChild(style);
  }
}
